import React, { memo, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { Chess } from 'chess.js';
import { HighlightChessboard, ThemeProvider } from 'react-chess-core';
import PreviewBoardPlaceholder from '../common/PreviewBoardPlaceholder';
import { nonInteractivePreviewBoardShellSx } from '../common/previewBoardShell';
import { useDocumentVisible } from '../common/useDocumentVisible';
import { fenLineFromOpeningPgn, pgnMovetextToSans } from 'react-chess-core';
import { PREVIEW_BOARD_WIDTH } from '../common/previewBoardSizes';
import { useUserSettings } from '../settings/useUserSettings';
import {
  boardOrientationForTrainSide,
  firstPlyTrainSide,
} from './courseLineUtils';
import { COURSE_PREVIEW_LINE_PLIES, type TrainSide } from './types';

const MOVE_MS = 650;
const LOOP_PAUSE_MS = 900;
const MOVE_ANIMATION_MS = 220;

const lastMoveUciAtPreviewPly = (
  pgn: string,
  startFen: string | undefined,
  setupUci: string | undefined,
  ply: number,
): string | null => {
  if (ply <= 0) {
    return setupUci ?? null;
  }

  const chess = startFen ? new Chess(startFen) : new Chess();
  const sans = pgnMovetextToSans(pgn);
  let last: string | null = setupUci ?? null;
  for (let i = 0; i < ply && i < sans.length; i += 1) {
    const move = chess.move(sans[i]!);
    if (!move) {
      break;
    }
    last = `${move.from}${move.to}${move.promotion ?? ''}`;
  }
  return last;
};

type CoursePreviewLineBoardProps = {
  pgn?: string;
  startFen?: string;
  setupUci?: string;
  trainSide?: TrainSide;
  boardWidth?: number;
  /** Stagger auto-play start so boards do not move in lockstep. */
  startDelayMs?: number;
  /** Slide pieces between plies. Disable for fixed-position hover popups. */
  animate?: boolean;
  /** Cycle through preview plies. Set false for static browse tiles on touch. */
  autoplay?: boolean;
};

const CoursePreviewLineBoard = ({
  pgn,
  startFen,
  setupUci,
  trainSide,
  boardWidth = PREVIEW_BOARD_WIDTH,
  startDelayMs = 0,
  animate = true,
  autoplay = true,
}: CoursePreviewLineBoardProps) => {
  const { settings } = useUserSettings();
  const documentVisible = useDocumentVisible();
  const fens = useMemo(() => {
    if (!pgn?.trim()) {
      return [];
    }
    return fenLineFromOpeningPgn(pgn, startFen, COURSE_PREVIEW_LINE_PLIES);
  }, [pgn, startFen]);
  const [ply, setPly] = useState(0);
  const [animationDuration, setAnimationDuration] = useState(
    animate ? MOVE_ANIMATION_MS : 0,
  );

  useEffect(() => {
    setPly(0);
    setAnimationDuration(animate ? MOVE_ANIMATION_MS : 0);
  }, [animate, fens]);

  useEffect(() => {
    if (!autoplay || !documentVisible || fens.length <= 1) {
      return;
    }

    let cancelled = false;
    let plyLocal = 0;
    let timeoutId = 0;

    const step = () => {
      if (cancelled) {
        return;
      }

      const next = (plyLocal + 1) % fens.length;
      if (animate && next === 0) {
        setAnimationDuration(0);
        window.requestAnimationFrame(() => {
          if (!cancelled) {
            setAnimationDuration(MOVE_ANIMATION_MS);
          }
        });
      }

      plyLocal = next;
      setPly(plyLocal);

      const delay = next === 0 ? LOOP_PAUSE_MS : MOVE_MS;
      timeoutId = window.setTimeout(step, delay);
    };

    timeoutId = window.setTimeout(step, startDelayMs + MOVE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [animate, autoplay, documentVisible, fens, startDelayMs]);

  const staticPly = Math.max(0, fens.length - 1);
  const displayPly = autoplay ? ply : staticPly;
  const lastMoveUcis = useMemo(
    () =>
      pgn
        ? fens.map((_, index) =>
            lastMoveUciAtPreviewPly(pgn, startFen, setupUci, index),
          )
        : [],
    [fens, pgn, setupUci, startFen],
  );
  const fen = fens[displayPly];
  const lastMoveUci = lastMoveUcis[displayPly] ?? null;
  const boardOrientation = boardOrientationForTrainSide(
    trainSide ?? firstPlyTrainSide(startFen ?? fen ?? ''),
  );

  if (!fen || fens.length <= 1) {
    return <PreviewBoardPlaceholder boardWidth={boardWidth} />;
  }

  const boardShellSx = nonInteractivePreviewBoardShellSx(boardWidth);

  return (
    <Box sx={boardShellSx}>
      <ThemeProvider theme="dark" boardTheme={settings.boardTheme}>
        <HighlightChessboard
          position={fen}
          boardWidth={boardWidth}
          boardOrientation={boardOrientation}
          arePiecesDraggable={false}
          showBoardNotation={false}
          animationDuration={animationDuration}
          checkSquare=""
          hintSquare={null}
          incorrectMoveSquare={null}
          lastMoveUci={lastMoveUci}
        />
      </ThemeProvider>
    </Box>
  );
};

export default memo(CoursePreviewLineBoard);
